import type { PoiWindow } from "@trip/domain/poi";
import type { PaletteColor } from "@trip/domain/palette";
import { Check, ChevronDown, ChevronUp, Heart, Pencil, Trash2, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { ApiError } from "../lib/api.ts";
import { categoryName } from "../lib/category-icons.ts";
import {
  addComment,
  listComments,
  type Poi,
  type PoiCategory,
  type PoiComment,
  type ScoredPoi,
} from "../lib/pois.ts";
import { ActionMenu, ConfirmDialog, reason } from "../ui/index.ts";
import { LEVEL_WORDS } from "./DangerLayer.tsx";
import { Likers, likedSaid } from "./PoiLikes.tsx";
import { distanceSaid, urgencySaid } from "./PoiSheet.tsx";
import { useSnapHandle, type PoiSnap } from "./useSnapHandle.ts";

/** Monday first, as a week is read aloud; the API numbers Sunday 0. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** An hour of the day as a clock shows it: 17 → "17:00", 12.5 → "12:30", 24 → "24:00". */
function clock(h: number): string {
  const mins = Math.round(h * 60);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/** A window's days in words, runs joined by a dash: "Fri–Sun", "Mon, Wed"; null is every day. */
function daysSaid(days: number[] | null): string | null {
  const set = new Set((days ?? []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6));
  if (set.size === 0 || set.size === 7) return null;
  // Positions in the Monday-first week, so Fri, Sat, Sun are one run.
  const at = WEEK.flatMap((d, i) => (set.has(d) ? [i] : []));
  const runs: number[][] = [];
  for (const i of at) {
    const run = runs.at(-1);
    if (run && run.at(-1) === i - 1) run.push(i);
    else runs.push([i]);
  }
  return runs
    .map((r) => {
      const first = DAY[WEEK[r[0]!]!];
      return r.length === 1 ? first : `${first}–${DAY[WEEK[r.at(-1)!]!]}`;
    })
    .join(", ");
}

/**
 * A place's windows in words, one after the other: "Fri–Sun 17:00–22:00", "Every day
 * 22:00–02:00", "Sat all day". No windows, or none that ever close, is "Open any time".
 */
export function windowsSaid(windows: PoiWindow[]): string {
  const said = (Array.isArray(windows) ? windows : []).map((w) => {
    const days = daysSaid(w.days);
    const { startHour: a, endHour: b } = w;
    const hours = a === null || b === null || a === b ? null : `${clock(a)}–${clock(b)}`;
    if (days === null && hours === null) return null;
    return days === null ? `Every day ${hours}` : `${days} ${hours ?? "all day"}`;
  });
  return said.length === 0 || said.some((s) => s === null) ? "Open any time" : said.join(" · ");
}

/** A moment as the view prints it: "30 Oct, 12:05". */
const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Why a write failed, in words. A refusal for who the reader is (403) is the server's own
 * sentence, which says who may; the generic "ask the trip admin" would be wrong about removing.
 */
export const failure = (e: unknown) =>
  e instanceof ApiError && e.status === 403 ? e.message : reason(e as Error);

/** What deleting a place takes with it, said before it is done: the editor says the same. */
export const TAKES_ALONG = "Its hours, likes, visit and comments go with it.";

const scoredOf = (p: Poi | ScoredPoi): ScoredPoi | null => ("proximity" in p ? p : null);

/**
 * Layer 2's place view: everything the group knows about one place, in a panel over the bottom of
 * the map. Top to bottom: the name and its category; how far, and how safe its sector is; whether
 * it is time-sensitive, and its hours; like and visited, as two labelled buttons; who likes it;
 * whether it has been visited, by whom and when; the list's note. The header is close, the name,
 * and a menu holding Edit place and, for whoever may remove it, Delete place.
 *
 * Two stops, as the places sheet has: the card, which leaves about half the map in sight and
 * holds all of that, and the full stop, which adds the comments (oldest first) and a box for one
 * more. The way between them is the button at the foot of the card, "2 comments"; the handle on
 * top, a tap or a drag, does the same. The comments are asked for when the view opens, so the
 * button can say how many, and only shown when it is pressed.
 *
 * Nothing is shown done before the server says so: every write waits for its answer, and one
 * that fails leaves the view as it was and says why.
 */
export function PoiView({
  tripId,
  poi,
  category,
  me,
  now,
  fromCentre,
  nameOf,
  colorOf,
  onClose,
  onVote,
  onTick,
  onEdit,
  onRemove,
}: {
  tripId: string;
  /** The place, scored as the list scores it; unscored only while the danger data is loading. */
  poi: Poi | ScoredPoi;
  /** Its category, or null when it has none. */
  category: PoiCategory | null;
  /** The reader's user id, once known. */
  me: string | null;
  /** The clock the list was scored at, so the urgency agrees with it. */
  now: number;
  /** True when closeness was measured from the middle of the map, there being no fix. */
  fromCentre: boolean;
  /** Somebody's name on this trip, from their user id. */
  nameOf: (userId: string) => string;
  /** A member's colour on this trip, for their face; null for someone who has left. */
  colorOf?: ((userId: string) => PaletteColor | null) | undefined;
  onClose: () => void;
  /** Resolves once the server has the like (or its withdrawal); rejects when it does not. */
  onVote: (on: boolean) => Promise<void>;
  /** Resolves once the server has the tick (or the untick); rejects when it does not. */
  onTick: (on: boolean) => Promise<void>;
  /** Opens the place editor on this place. */
  onEdit: () => void;
  /**
   * Deletes the place; resolves once the server has, rejects when it has not. Given only to whoever
   * the server lets remove it (the adder, or a trip admin): where it is absent the menu has no
   * Delete at all, not a disabled one.
   */
  onRemove?: (() => Promise<void>) | undefined;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<PoiComment[] | null>(null);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [snap, setSnap] = useState<PoiSnap>("card");
  const open = snap === "full";
  const sheet = useRef<HTMLElement>(null);
  const handle = useSnapHandle({ sheet, open, onSnap: setSnap });
  const thread = useId();

  useEffect(() => {
    let live = true;
    setComments(null);
    setCommentsError(null);
    listComments(tripId, poi.id)
      .then((items) => {
        if (live) setComments(items);
      })
      .catch((e: unknown) => {
        if (!live) return;
        console.error("places: the comments could not be fetched", e);
        setCommentsError(`Comments could not be loaded. ${reason(e as Error)}`);
      });
    return () => {
      live = false;
    };
  }, [tripId, poi.id]);

  async function run(act: () => Promise<void>, failed: string) {
    setBusy(true);
    setError(null);
    try {
      await act();
    } catch (e) {
      console.error("places: a write from the place view did not reach the server", e);
      setError(`${failed} ${failure(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function post() {
    const body = draft.trim();
    if (body === "") return;
    setPosting(true);
    setPostError(null);
    try {
      const made = await addComment(tripId, poi.id, body);
      setComments((c) => [...(c ?? []), made]);
      setDraft("");
    } catch (e) {
      console.error("places: a comment did not reach the server", e);
      setPostError(`Comment not posted. ${failure(e)}`);
    } finally {
      setPosting(false);
    }
  }

  const scored = scoredOf(poi);
  const where = [
    scored ? distanceSaid(scored, fromCentre) : null,
    scored === null
      ? null
      : scored.level === null
        ? "No safety data"
        : `Safety: ${scored.level} of 6 · ${LEVEL_WORDS[scored.level - 1]}`,
  ].filter((s): s is string => s !== null);
  const urgency = urgencySaid(poi.windows, now).text;
  const windows = windowsSaid(poi.windows);

  const liked = likedSaid(poi, me);

  // The fetched thread once it is in, the server's count until then.
  const count = comments?.length ?? poi.commentCount;
  const commentsSaid =
    count === 0 ? "Be the first to comment" : `${count} ${count === 1 ? "comment" : "comments"}`;

  const ticked = poi.ticked
    ? `Visited by ${poi.ticked.by === me ? "you" : nameOf(poi.ticked.by)}, ${when(poi.ticked.at)}`
    : "Not visited yet";

  return (
    <section ref={sheet} className="poi-editor poi-view" aria-label={poi.name} data-snap={snap}>
      <button
        type="button"
        className="poi-sheet-handle"
        aria-label={open ? "Shrink place view" : "Expand place view"}
        aria-expanded={open}
        {...handle}
      >
        <span className="poi-sheet-grip" aria-hidden="true" />
      </button>
      {/* Close at the left, the name between, the menu at the right: the name is the header. */}
      <header className="poi-editor-head poi-view-head">
        <button
          type="button"
          className="icon-button poi-editor-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} strokeWidth={1.75} aria-hidden="true" />
        </button>
        <h2 className="poi-editor-title poi-view-name">
          {poi.ticked ? <s>{poi.name}</s> : poi.name}
        </h2>
        <ActionMenu
          label={`${poi.name} actions`}
          floating
          disabled={busy}
          actions={[
            { label: "Edit place", icon: <Pencil size={16} strokeWidth={1.75} />, onPick: onEdit },
            ...(onRemove
              ? [
                  {
                    label: "Delete place",
                    icon: <Trash2 size={16} strokeWidth={1.75} />,
                    destructive: true,
                    onPick: () => setAsking(true),
                  },
                ]
              : []),
          ]}
        />
      </header>
      <div className="poi-editor-body poi-view-body">
        <div className="poi-view-meta">
          {category && (
            <span
              className="poi-badge"
              style={{ "--badge": category.color } as CSSProperties}
            >{`${category.emoji} ${categoryName(category.name)}`}</span>
          )}
          {where.length > 0 && <p className="small">{where.join(" · ")}</p>}
          <p className="small">
            {urgency && <strong className="poi-view-urgency">{urgency} · </strong>}
            {windows}
          </p>
        </div>
        {/* Like and visited, labelled, side by side across the width. */}
        <div className="poi-view-actions" role="group" aria-label={`${poi.name} controls`}>
          <button
            type="button"
            className="poi-vote poi-action"
            aria-pressed={poi.voted}
            disabled={busy}
            onClick={() => void run(() => onVote(!poi.voted), "Like not saved.")}
          >
            <Heart size={20} strokeWidth={1.75} aria-hidden="true" />
            Like
          </button>
          <button
            type="button"
            className="poi-tick poi-action"
            aria-pressed={poi.ticked !== null}
            disabled={busy}
            onClick={() => void run(() => onTick(poi.ticked === null), "Visited not saved.")}
          >
            <Check size={20} strokeWidth={2} aria-hidden="true" />
            Visited
          </button>
        </div>
        <div className="poi-view-likes">
          <Likers voters={poi.voters} me={me} colorOf={colorOf} />
          <p className="small">{liked}</p>
        </div>
        <p className="small">{ticked}</p>
        {poi.note && <p className="small poi-view-note">{poi.note}</p>}

        <button
          type="button"
          className="poi-view-comments"
          aria-expanded={open}
          aria-controls={thread}
          onClick={() => setSnap(open ? "card" : "full")}
        >
          {commentsSaid}
          {open ? (
            <ChevronUp size={20} strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <ChevronDown size={20} strokeWidth={1.75} aria-hidden="true" />
          )}
        </button>

        <div id={thread} hidden={!open} className="poi-view-thread">
          {open && (
            <>
              {commentsError && (
                <p role="alert" className="error small">
                  {commentsError}
                </p>
              )}
              {comments === null && !commentsError && (
                <p className="small muted">Loading comments…</p>
              )}
              {comments !== null && comments.length === 0 && (
                <p className="small muted">No comments yet</p>
              )}
              {comments !== null && comments.length > 0 && (
                <ul className="plain poi-comments" aria-label="Comments">
                  {comments.map((c) => (
                    <li key={c.id} className="poi-comment">
                      <span className="small">
                        <strong>{c.userId === me ? "You" : c.name}</strong>{" "}
                        <span className="muted">{when(c.createdAt)}</span>
                      </span>
                      <p className="small poi-view-note">{c.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              {/* The box and its Post on one row, last in the scrolling body. */}
              <div className="poi-view-compose">
                <label className="poi-editor-field">
                  <span className="sr">Add a comment</span>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    placeholder="Add a comment"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="button small"
                  aria-busy={posting || undefined}
                  disabled={posting || draft.trim() === ""}
                  onClick={() => void post()}
                >
                  Post
                </button>
              </div>
              {postError && (
                <p role="alert" className="error small">
                  {postError}
                </p>
              )}
            </>
          )}
        </div>
      </div>
      {/* By the actions it is about, and outside the scrolling body, so it is never out of sight. */}
      {error && (
        <p role="alert" className="error small poi-view-error">
          {error}
        </p>
      )}
      {onRemove && (
        <ConfirmDialog
          open={asking}
          title={`Delete ${poi.name}?`}
          body={<p>{TAKES_ALONG}</p>}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            setAsking(false);
            void run(onRemove, "Not deleted.");
          }}
          onCancel={() => setAsking(false)}
        />
      )}
    </section>
  );
}
